import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { pad2, snapToStep, atMinutes } from '../../TimeStepper';
import { useTimePickerBounds } from '../timePickerShared';
import styles from '../CustomTimePicker.module.scss';

const MENU_MAX_HEIGHT = 280;

const DesktopTimePicker = ({
  value,
  setValue,
  placeholder,
  title,
  minTime,
  maxTime,
  error,
  disabled = false,
  timeStep = 5,
}) => {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const hourActiveRef = useRef(null);
  const minuteActiveRef = useRef(null);
  const didCenterRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const { step, safeValue, boundOpts, currentTotal, display } = useTimePickerBounds(
    value,
    minTime,
    maxTime,
    timeStep,
  );

  const currentHour = Math.floor(currentTotal / 60);
  const currentMinute = currentTotal % 60;

  const hours = useMemo(() => {
    const list = [];
    for (let hour = 0; hour <= 23; hour += 1) {
      const hourStart = hour * 60;
      const hourEnd = hour * 60 + 60 - step;
      if (hourEnd < boundOpts.min || hourStart > boundOpts.max) continue;
      list.push(hour);
    }
    return list;
  }, [boundOpts, step]);

  const minutes = useMemo(() => {
    const list = [];
    for (let minute = 0; minute < 60; minute += step) {
      const total = currentHour * 60 + minute;
      if (total < boundOpts.min || total > boundOpts.max) continue;
      list.push(minute);
    }
    return list;
  }, [currentHour, step, boundOpts]);

  const close = useCallback(() => setOpen(false), []);

  const applyTotal = useCallback(
    (total, shouldClose = false) => {
      if (disabled) return;
      setValue?.(atMinutes(safeValue, total, boundOpts));
      if (shouldClose) setOpen(false);
    },
    [disabled, setValue, safeValue, boundOpts],
  );

  const selectHour = useCallback(
    (hour) => {
      let next = hour * 60 + currentMinute;
      if (next < boundOpts.min) next = boundOpts.min;
      if (next > boundOpts.max) next = boundOpts.max;
      const snapped = snapToStep(next, step);
      const sameHour = Math.floor(snapped / 60) === hour;
      applyTotal(
        sameHour
          ? snapped
          : snapToStep(Math.max(boundOpts.min, Math.min(boundOpts.max, hour * 60)), step),
      );
    },
    [currentMinute, boundOpts, step, applyTotal],
  );

  const selectMinute = useCallback(
    (minute) => {
      applyTotal(currentHour * 60 + minute, true);
    },
    [applyTotal, currentHour],
  );

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, 220);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(MENU_MAX_HEIGHT, Math.max(180, openUp ? spaceAbove : spaceBelow));
    const next = {
      position: 'fixed',
      left: Math.max(8, left),
      width,
      height: maxHeight,
      maxHeight,
      zIndex: 10000,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 6, top: 'auto' }
        : { top: rect.bottom + 6, bottom: 'auto' }),
    };

    setMenuStyle((prev) => {
      if (
        prev &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.top === next.top &&
        prev.bottom === next.bottom
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onResize = () => updateMenuPosition();
    const onScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) close();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open) {
      didCenterRef.current = false;
      return undefined;
    }
    if (!menuStyle || didCenterRef.current) return undefined;
    didCenterRef.current = true;
    const scrollActive = (el) => {
      const list = el?.parentElement;
      if (!el || !list) return;
      const top = el.offsetTop - list.clientHeight / 2 + el.clientHeight / 2;
      list.scrollTop = Math.max(0, top);
    };
    scrollActive(hourActiveRef.current);
    scrollActive(minuteActiveRef.current);
    return undefined;
  }, [open, menuStyle]);

  const stopWheelBubble = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const menu = open && menuStyle
    ? createPortal(
      <div
        ref={menuRef}
        className={styles.menu}
        style={menuStyle}
        role="dialog"
        aria-label={title || placeholder || 'Orario'}
        onWheel={stopWheelBubble}
        onTouchMove={stopWheelBubble}
      >
        <div className={styles.columns}>
          <div className={styles.column}>
            <span className={styles.column_label}>Ora</span>
            <div className={styles.column_list} role="listbox" aria-label="Ora">
              {hours.map((hour) => {
                const active = hour === currentHour;
                return (
                  <button
                    key={hour}
                    type="button"
                    role="option"
                    aria-selected={active}
                    ref={active ? hourActiveRef : null}
                    className={clx(styles.option, { [styles.option_active]: active })}
                    onClick={() => selectHour(hour)}
                  >
                    {pad2(hour)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.column_divider} aria-hidden />

          <div className={styles.column}>
            <span className={styles.column_label}>Min</span>
            <div className={styles.column_list} role="listbox" aria-label="Minuti">
              {minutes.map((minute) => {
                const active = minute === currentMinute;
                return (
                  <button
                    key={minute}
                    type="button"
                    role="option"
                    aria-selected={active}
                    ref={active ? minuteActiveRef : null}
                    className={clx(styles.option, { [styles.option_active]: active })}
                    onClick={() => selectMinute(minute)}
                  >
                    {pad2(minute)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div
      ref={rootRef}
      className={clx(styles.picker_root, {
        [styles.picker_open]: open,
        [styles.picker_error]: !!error,
        [styles.picker_disabled]: disabled,
      })}
    >
      {(title || placeholder) && <span className={styles.label}>{title || placeholder}</span>}

      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <span className={styles.trigger_value}>{display}</span>
        <ChevronDown size={18} strokeWidth={2.3} className={styles.trigger_chevron} aria-hidden />
      </button>

      {menu}

      {error ? <span className={styles.error_text}>{error}</span> : null}
    </div>
  );
};

export default DesktopTimePicker;
